import { useState } from 'react';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TablePagination from '@mui/material/TablePagination';
import { flexRender } from '@tanstack/react-table';
import {
  AppTable,
  AppTableBody,
  AppTableCell,
  AppTableContainer,
  AppTableHead,
  AppTableRow,
  AppTableSort,
} from '../../../../components/common/app-table/ui';
import useDefaultTable from '../../../../components/common/app-table/useDefaultTable';
import LoadingIndicator from '../../../../components/common/LoadingIndicator';
import AppSearchField from '../../../../components/common/forms/AppSearchField';
import EURLogsColumns from './Columns';
import { ExportButton } from '../../../../components/common/primebmd-ui/DataButtons';
import { IEUR, IEURColumns } from '../utils/types';
import { exportEURToExcel } from '../utils/exporting';
import { useGetEURSMRQuery } from '../../../../hooks/useEURSMR';
import { useExportEUR } from '../../../../hooks/useHistoricalEURs';
import { usePermission } from "../../../../hooks/usePermissions";
import { Modules, Permissions } from "../../../Users/Roles/roles-utils";
import { Navigate } from 'react-router-dom';
import ImagePreviewer from '../../../../components/common/ImagePreviewer';

const EURLogsTable = () => {
  const hasPermission = usePermission(Modules.repHeur.accessKey);
  const hasExportPermission = usePermission(
    Modules.repHeur.accessKey,
    Permissions.export
  );
  const [startDate] = useState<string>('');
  const [endDate] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState<(string | File)[]>([]);

  const eur = useGetEURSMRQuery(startDate, endDate);
  const data: IEUR[] = eur?.data === undefined ? [] : eur?.data;
  const formattedData: IEURColumns[] = useExportEUR(data);

  const handlePreview = (imgs: (string | File)[]) => {
    setImages(imgs);
    setOpen(true);
  };

  const { table, dataCount, searchFilter, setSearchFilter, pageIndex, pageSize } =
  useDefaultTable<IEURColumns>(
    formattedData, 
    EURLogsColumns(handlePreview)
  );

  if (!hasPermission) return <Navigate to="/home" />;

  const emptyText = searchFilter ? 'No data found' : 'No data yet';

  const handleExport = () => {
    const dataToExport = formattedData.map((item) => {
      const { startingSmrImage, endingSmrImage, ...rest } = item;
      return rest;
    });

    exportEURToExcel(dataToExport, 'Historical EURs');
  }

  if (eur?.isLoading) return <LoadingIndicator show />;

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2} gap={2}>
        <AppSearchField
          onSearchKeywordChange={setSearchFilter}
          searchKeyword={searchFilter ?? ''}
        />
        <Stack direction="row" gap={.75}>
          {hasExportPermission && <ExportButton onClick={handleExport} />}
        </Stack>
      </Stack>
      <Paper elevation={3}>
        <AppTableContainer>
          <AppTable stickyHeader>
            <AppTableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <AppTableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <AppTableCell key={header.id} align='center'>
                      <AppTableSort
                        canSort={header.column.getCanSort()}
                        currentSort={header.column.getIsSorted()}
                        onSort={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </AppTableSort>
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableHead>
            <AppTableBody
              dataCount={dataCount}
              // loading={loading}
              emptyText={emptyText}
            >
              {table.getRowModel().rows.map((row) => (
                <AppTableRow key={row.id} sx={{ height: '40px' }}>
                  {row.getVisibleCells().map((cell) => (
                    <AppTableCell key={cell.id} align='center'>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </AppTableCell>
                  ))}
                </AppTableRow>
              ))}
            </AppTableBody>
          </AppTable>
        </AppTableContainer>
        <Box position='relative'>
          <TablePagination
            sx={{ bgcolor: 'white', borderTop: '1px solid #eee' }}
            component='div'
            count={dataCount}
            page={pageIndex}
            onPageChange={(e, page) => table.setPageIndex(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Box>
      </Paper>
      <ImagePreviewer
        open={open}
        images={images}
        initialIndex={open ? 0 : -1}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export default EURLogsTable;